// IndexedDB module for offline data storage
import type { ItemData, ListData } from './providers/supabase/api';

const DB_NAME = 'shopman-db';
const DB_VERSION = 1;

// Store names
const STORES = {
  LISTS: 'lists',
  ITEMS: 'items',
  SYNC_QUEUE: 'syncQueue',
} as const;

type StoreName = typeof STORES[keyof typeof STORES];

// Extended types for local storage
// Notification preferences for granular control
export interface NotificationPreferences {
  itemAdded: boolean;
  itemBought: boolean;
  itemDeleted: boolean;
  listArchived: boolean;
}

export const DEFAULT_NOTIFICATION_PREFERENCES: NotificationPreferences = {
  itemAdded: true,
  itemBought: true,
  itemDeleted: false,
  listArchived: true,
};

export interface LocalListData extends ListData {
  name_lowercase: string;
  syncStatus: SyncStatus;
  lastModified: number;
  isFollowed?: boolean;
  notificationsEnabled?: boolean;
  notificationPreferences?: NotificationPreferences;
  followedAt?: number;
  savedPassword?: string | null;
  pushSubscription?: PushSubscriptionJSON | null;
  // Translation fields are inherited from ListData:
  // - shopping_country?: string | null (target country for translation)
  // - list_language?: string (source language, default "unknown")
}

export interface LocalItemData extends ItemData {
  syncStatus: SyncStatus;
  lastModified: number;
  sort_order?: number;
  translated_name?: string | null; // Cached translated name
  detected_lang?: string; // Language detected during parsing
}

export type SyncStatus = 'synced' | 'pending' | 'error';

export interface SyncQueueOperation {
  id?: number;
  type: 'ADD_ITEM' | 'UPDATE_ITEM' | 'TOGGLE_ITEM' | 'DELETE_ITEM';
  data: Record<string, unknown>;
  timestamp: number;
  retries: number;
}

let dbInstance: IDBDatabase | null = null;

// Initialize the database
async function initDB(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('[DB] Failed to open database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      dbInstance = request.result;
      console.log('[DB] Database opened successfully');
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      console.log('[DB] Upgrading database...');

      // Lists store
      if (!db.objectStoreNames.contains(STORES.LISTS)) {
        const listsStore = db.createObjectStore(STORES.LISTS, { keyPath: 'id' });
        listsStore.createIndex('name', 'name_lowercase', { unique: true });
        listsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Items store
      if (!db.objectStoreNames.contains(STORES.ITEMS)) {
        const itemsStore = db.createObjectStore(STORES.ITEMS, { keyPath: 'id' });
        itemsStore.createIndex('listId', 'list_id', { unique: false });
        itemsStore.createIndex('syncStatus', 'syncStatus', { unique: false });
      }

      // Sync queue store for pending operations
      if (!db.objectStoreNames.contains(STORES.SYNC_QUEUE)) {
        const syncStore = db.createObjectStore(STORES.SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
        syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        syncStore.createIndex('type', 'type', { unique: false });
      }

      console.log('[DB] Database upgrade complete');
    };
  });
}

// Generic database operations
async function getStore(storeName: StoreName, mode: IDBTransactionMode = 'readonly'): Promise<IDBObjectStore> {
  const db = await initDB();
  const transaction = db.transaction(storeName, mode);
  return transaction.objectStore(storeName);
}

async function dbGet<T>(storeName: StoreName, key: string | number): Promise<T | undefined> {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result as T | undefined);
    request.onerror = () => reject(request.error);
  });
}

async function dbGetAll<T>(storeName: StoreName): Promise<T[]> {
  const store = await getStore(storeName);
  return new Promise((resolve, reject) => {
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

async function dbGetByIndex<T>(storeName: StoreName, indexName: string, value: string): Promise<T[]> {
  const store = await getStore(storeName);
  const index = store.index(indexName);
  return new Promise((resolve, reject) => {
    const request = index.getAll(value);
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
}

async function dbPut<T>(storeName: StoreName, data: T): Promise<IDBValidKey> {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.put(data);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function dbDelete(storeName: StoreName, key: string | number): Promise<void> {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function dbClear(storeName: StoreName): Promise<void> {
  const store = await getStore(storeName, 'readwrite');
  return new Promise((resolve, reject) => {
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Database service object
const db = {
  async init(): Promise<void> {
    await initDB();
  },

  // Lists
  async getList(listId: string): Promise<LocalListData | undefined> {
    return dbGet<LocalListData>(STORES.LISTS, listId);
  },

  async getListByName(name: string): Promise<LocalListData | null> {
    const lists = await dbGetByIndex<LocalListData>(STORES.LISTS, 'name', name.toLowerCase());
    return lists[0] || null;
  },

  async saveList(list: Partial<LocalListData> & { id: string; name: string }): Promise<LocalListData> {
    // First, check if this list already exists to preserve local-only settings
    const existingList = await dbGet<LocalListData>(STORES.LISTS, list.id);

    // Local-only fields that should persist across server updates
    const localSettings = existingList ? {
      isFollowed: existingList.isFollowed,
      notificationsEnabled: existingList.notificationsEnabled,
      followedAt: existingList.followedAt,
      savedPassword: existingList.savedPassword,
      pushSubscription: existingList.pushSubscription,
    } : {};

    const data: LocalListData = {
      // Start with preserved local settings (if any)
      ...localSettings,
      // Then apply incoming data (server data or new data)
      ...list,
      // Finally, set computed/default fields
      name_lowercase: list.name.toLowerCase(),
      syncStatus: list.syncStatus || 'synced',
      lastModified: Date.now(),
      has_access_password: list.has_access_password ?? false,
      has_admin_password: list.has_admin_password ?? false,
      view_requires_password: list.view_requires_password ?? false,
      edit_requires_password: list.edit_requires_password ?? false,
      created_at: list.created_at ?? existingList?.created_at ?? new Date().toISOString(),
      list_access_password_hash: list.list_access_password_hash ?? null,
      list_admin_password_hash: list.list_admin_password_hash ?? null,
    } as LocalListData;
    await dbPut(STORES.LISTS, data);
    return data;
  },

  async deleteList(listId: string): Promise<void> {
    // Delete all items for this list first
    const items = await this.getItemsByList(listId);
    for (const item of items) {
      await dbDelete(STORES.ITEMS, item.id);
    }
    await dbDelete(STORES.LISTS, listId);
  },

  async getAllLists(): Promise<LocalListData[]> {
    return dbGetAll<LocalListData>(STORES.LISTS);
  },

  // Items
  async getItem(itemId: string): Promise<LocalItemData | undefined> {
    return dbGet<LocalItemData>(STORES.ITEMS, itemId);
  },

  async getItemsByList(listId: string): Promise<LocalItemData[]> {
    return dbGetByIndex<LocalItemData>(STORES.ITEMS, 'listId', listId);
  },

  async saveItem(item: Partial<LocalItemData> & { id: string; list_id: string; name: string }): Promise<LocalItemData> {
    const data: LocalItemData = {
      ...item,
      syncStatus: item.syncStatus || 'synced',
      lastModified: Date.now(),
      amount: item.amount ?? null,
      type: item.type ?? null,
      note: item.note ?? null,
      is_bought: item.is_bought ?? false,
      created_at: item.created_at ?? new Date().toISOString(),
    } as LocalItemData;
    await dbPut(STORES.ITEMS, data);
    return data;
  },

  async saveItems(items: Array<Partial<LocalItemData> & { id: string; list_id: string; name: string }>): Promise<void> {
    for (const item of items) {
      await this.saveItem(item);
    }
  },

  async deleteItem(itemId: string): Promise<void> {
    await dbDelete(STORES.ITEMS, itemId);
  },

  // Delete many items in ONE readwrite transaction instead of one transaction
  // per id (the previous per-row pattern in syncListItems' purge-loop and
  // deleteItemsByList). No-op on an empty list.
  async batchDeleteItems(itemIds: string[]): Promise<void> {
    if (itemIds.length === 0) return;
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.ITEMS, 'readwrite');
      const store = tx.objectStore(STORES.ITEMS);
      for (const id of itemIds) store.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },

  async deleteItemsByList(listId: string): Promise<void> {
    const items = await this.getItemsByList(listId);
    await this.batchDeleteItems(items.map(item => item.id));
  },

  // Sync Queue operations
  async addToSyncQueue(operation: Omit<SyncQueueOperation, 'id' | 'timestamp' | 'retries'>): Promise<SyncQueueOperation> {
    const data: Omit<SyncQueueOperation, 'id'> = {
      ...operation,
      timestamp: Date.now(),
      retries: 0,
    };
    const id = await dbPut(STORES.SYNC_QUEUE, data);
    return { ...data, id: id as number };
  },

  async getSyncQueue(): Promise<SyncQueueOperation[]> {
    const items = await dbGetAll<SyncQueueOperation>(STORES.SYNC_QUEUE);
    // Sort by timestamp
    return items.sort((a, b) => a.timestamp - b.timestamp);
  },

  async removeSyncQueueItem(id: number): Promise<void> {
    await dbDelete(STORES.SYNC_QUEUE, id);
  },

  async clearSyncQueue(): Promise<void> {
    await dbClear(STORES.SYNC_QUEUE);
  },

  async updateSyncQueueItem(id: number, updates: Partial<SyncQueueOperation>): Promise<void> {
    const item = await dbGet<SyncQueueOperation>(STORES.SYNC_QUEUE, id);
    if (item) {
      await dbPut(STORES.SYNC_QUEUE, { ...item, ...updates });
    }
  },

  // Put many sync-queue ops in ONE readwrite transaction instead of a get+put
  // round-trip per op (the previous resetAuthRetries loop did 2 transactions
  // per row). Callers pass the already-updated full op objects. No-op on empty.
  async batchPutSyncQueue(ops: SyncQueueOperation[]): Promise<void> {
    if (ops.length === 0) return;
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORES.SYNC_QUEUE, 'readwrite');
      const store = tx.objectStore(STORES.SYNC_QUEUE);
      for (const op of ops) store.put(op);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  },

  // Get pending items (not yet synced)
  async getPendingItems(): Promise<LocalItemData[]> {
    return dbGetByIndex<LocalItemData>(STORES.ITEMS, 'syncStatus', 'pending');
  },

  // Get all items with sync errors
  async getFailedItems(): Promise<LocalItemData[]> {
    return dbGetByIndex<LocalItemData>(STORES.ITEMS, 'syncStatus', 'error');
  },

  // Mark item as synced
  async markItemSynced(itemId: string, serverId: string | null = null): Promise<void> {
    const item = await this.getItem(itemId);
    if (item) {
      item.syncStatus = 'synced';
      if (serverId && item.id !== serverId) {
        // Update local ID to match server ID
        await dbDelete(STORES.ITEMS, item.id);
        item.id = serverId;
      }
      await dbPut(STORES.ITEMS, item);
    }
  },

  // Generate temporary offline ID
  generateTempId(): string {
    return `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  },

  // Check if ID is temporary
  isTempId(id: string): boolean {
    return Boolean(id && id.toString().startsWith('temp_'));
  },

  // ===== Following lists =====

  // Follow a list (enables offline access)
  async followList(listId: string, options: { notifications?: boolean } = {}): Promise<LocalListData | null> {
    const list = await this.getList(listId);
    if (list) {
      list.isFollowed = true;
      list.notificationsEnabled = options.notifications || false;
      list.followedAt = Date.now();
      await dbPut(STORES.LISTS, list);
      return list;
    }
    return null;
  },

  // Unfollow a list
  async unfollowList(listId: string): Promise<void> {
    const list = await this.getList(listId);
    if (list) {
      list.isFollowed = false;
      list.notificationsEnabled = false;
      list.savedPassword = null; // Clear saved password
      await dbPut(STORES.LISTS, list);

      // Optionally delete cached items
      await this.deleteItemsByList(listId);
    }
  },

  // Get all followed lists
  async getFollowedLists(): Promise<LocalListData[]> {
    const allLists = await this.getAllLists();
    return allLists.filter(list => list.isFollowed === true);
  },

  // Check if a list is followed
  async isListFollowed(listId: string): Promise<boolean> {
    const list = await this.getList(listId);
    return list?.isFollowed === true;
  },

  // Toggle notifications for a list
  async setListNotifications(listId: string, enabled: boolean): Promise<LocalListData | null> {
    const list = await this.getList(listId);
    if (list) {
      list.notificationsEnabled = enabled;
      // Initialize preferences with defaults if not set
      if (!list.notificationPreferences) {
        list.notificationPreferences = { ...DEFAULT_NOTIFICATION_PREFERENCES };
      }
      await dbPut(STORES.LISTS, list);
      return list;
    }
    return null;
  },

  // Update notification preferences for a list
  async setNotificationPreferences(listId: string, preferences: NotificationPreferences): Promise<LocalListData | null> {
    const list = await this.getList(listId);
    if (list) {
      list.notificationPreferences = preferences;
      await dbPut(STORES.LISTS, list);
      return list;
    }
    return null;
  },

  // Get notification preferences for a list
  async getNotificationPreferences(listId: string): Promise<NotificationPreferences> {
    const list = await this.getList(listId);
    return list?.notificationPreferences || { ...DEFAULT_NOTIFICATION_PREFERENCES };
  },

  // ===== Password management =====

  // Save password for a list (encrypted in real app, here just base64 for demo)
  async savePassword(listId: string, password: string): Promise<void> {
    const list = await this.getList(listId);
    if (list) {
      // Note: In production, use proper encryption
      list.savedPassword = btoa(password);
      await dbPut(STORES.LISTS, list);
    }
  },

  // Get saved password for a list
  async getSavedPassword(listId: string): Promise<string | null> {
    const list = await this.getList(listId);
    if (list?.savedPassword) {
      try {
        return atob(list.savedPassword);
      } catch {
        return null;
      }
    }
    return null;
  },

  // Clear saved password for a list
  async clearSavedPassword(listId: string): Promise<void> {
    const list = await this.getList(listId);
    if (list) {
      list.savedPassword = null;
      await dbPut(STORES.LISTS, list);
    }
  },

  // ===== Push subscription =====

  // Save push subscription for a list
  async savePushSubscription(listId: string, subscription: PushSubscriptionJSON | null): Promise<void> {
    const list = await this.getList(listId);
    if (list) {
      list.pushSubscription = subscription;
      await dbPut(STORES.LISTS, list);
    }
  },

  // Get push subscription for a list
  async getPushSubscription(listId: string): Promise<PushSubscriptionJSON | null> {
    const list = await this.getList(listId);
    return list?.pushSubscription || null;
  },

  // ===== Item ordering =====

  // Update the sort order of items
  async updateItemsOrder(itemIds: string[]): Promise<void> {
    for (let i = 0; i < itemIds.length; i++) {
      const item = await this.getItem(itemIds[i]);
      if (item) {
        item.sort_order = i;
        item.lastModified = Date.now();
        await dbPut(STORES.ITEMS, item);
      }
    }
  },

  // Get items by list, sorted by sort_order
  async getItemsByListSorted(listId: string): Promise<LocalItemData[]> {
    const items = await this.getItemsByList(listId);
    return items.sort((a, b) => {
      // Items with sort_order come first, then by created_at
      const aOrder = a.sort_order ?? Number.MAX_SAFE_INTEGER;
      const bOrder = b.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });
  },
};

export default db;
